import React from 'react';
import { Button } from 'primereact/button';
import { LectureLecturer } from '../../../../../api/types/lecturers/lectures/lecture-lecturer';

interface MyLectureActionsProps {
  lecture: LectureLecturer;
  onStartLecture: (lecture: LectureLecturer) => void;
  onFinishLecture: (lecture: LectureLecturer) => void;
  onUpdateAttendance: (lecture: LectureLecturer) => void;
}

// Check if start button should be enabled
const canStartLecture = (lecture: LectureLecturer): boolean => {
  // If lecture already started, disable the start button
  if (lecture.actualStartTime !== null) {
    return false;
  }

  const now = new Date();
  const startTime = new Date(lecture.startTime);
  const endTime = new Date(lecture.endTime);
  const oneHourBefore = new Date(startTime.getTime() - 60 * 60 * 1000);
  // Allow start until one minute before the planned end time (e.g. 10:00–12:00 → until 11:59).
  const oneMinuteBeforeEnd = new Date(endTime.getTime() - 60 * 1000);

  return now >= oneHourBefore && now <= oneMinuteBeforeEnd;
};

// Check if finish button should be enabled
const canFinishLecture = (lecture: LectureLecturer): boolean => {
  // If lecture already finished, disable the finish button
  if (lecture.actualEndTime !== null) {
    return false;
  }

  const now = new Date();
  const endTime = new Date(lecture.endTime);
  const oneHourAfter = new Date(endTime.getTime() + 60 * 60 * 1000);

  return now <= oneHourAfter;
};

export const MyLectureActions: React.FC<MyLectureActionsProps> = ({
  lecture,
  onStartLecture,
  onFinishLecture,
  onUpdateAttendance,
}) => {
  const hasStarted = lecture.actualStartTime !== null;
  const hasFinished = lecture.actualEndTime !== null;
  const canStart = canStartLecture(lecture);
  const canFinish = canFinishLecture(lecture);
  const canUpdateAttendance = hasStarted; // Can update attendance after lecture has started

  const startTooltip = canStart
    ? 'לחץ להתחלת ההרצאה'
    : 'ניתן להתחיל הרצאה משעה לפני זמן ההתחלה המתוכנן ועד דקה לפני זמן הסיום המתוכנן';

  const finishTooltip = canFinish
    ? 'לחץ לסיום ההרצאה'
    : 'ניתן לסגור את ההרצאה עד שעה אחרי הזמן המתוכנן';

  const updateAttendanceTooltip = canUpdateAttendance
    ? 'לחץ לעדכון נתוני נוכחות'
    : 'ניתן לעדכן נוכחות רק לאחר התחלת ההרצאה';

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <Button
        label="התחל"
        size="small"
        tooltip={startTooltip}
        tooltipOptions={{
          showOnDisabled: true,
        }}
        severity={hasStarted ? 'secondary' : 'success'}
        disabled={!canStart}
        onClick={() => onStartLecture(lecture)}
        style={{
          minWidth: '60px',
          ...(hasStarted && {
            backgroundColor: '#6c757d',
            borderColor: '#6c757d',
            color: '#fff',
            opacity: 0.6,
          }),
        }}
      />

      <Button
        label="סיום"
        size="small"
        tooltip={finishTooltip}
        tooltipOptions={{
          showOnDisabled: true,
        }}
        severity={hasFinished ? 'secondary' : 'danger'}
        disabled={!canFinish}
        onClick={() => onFinishLecture(lecture)}
        style={{
          minWidth: '60px',
          ...(hasFinished && {
            backgroundColor: '#6c757d',
            borderColor: '#6c757d',
            color: '#fff',
            opacity: 0.6,
          }),
        }}
      />

      <Button
        label="עדכון נוכחות"
        size="small"
        tooltip={updateAttendanceTooltip}
        tooltipOptions={{
          showOnDisabled: true,
        }}
        severity="info"
        disabled={!canUpdateAttendance}
        onClick={() => onUpdateAttendance(lecture)}
        style={{
          minWidth: '90px',
        }}
      />
    </div>
  );
};
